import React, { useCallback, useEffect, useMemo, useState, Suspense } from "react";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Spinner,
    Badge,
    Flex,
    Text,
    useColorModeValue,
    Divider
} from "@chakra-ui/react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

const MyAccordionNested = ({
    list = [],
    urlBasePath = "/",
    includeALias = true,
    includeAgraphy = false,
    tabSegmentPosition = 2,
    subTabSegmentPosition = 3
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { alias } = useParams();

    // Ensure list is always an array
    const safeList = useMemo(() =>
        Array.isArray(list) ? list : [],
        [list]
    );

    // Extract path parts safely
    const pathParts = useMemo(() => {
        try {
            return location.pathname?.split("/")?.filter(Boolean) || [];
        } catch (error) {
            console.error("Error parsing path:", error);
            return [];
        }
    }, [location.pathname]);

    // Determine current tab ID with fallback
    const tab = useMemo(() => {
        const tabFromPath = pathParts[tabSegmentPosition];
        if (tabFromPath && safeList.some(item => item.id === tabFromPath)) {
            return tabFromPath;
        }
        return safeList?.[0]?.id || "default";
    }, [pathParts, safeList, tabSegmentPosition]);

    // Determine current subtab ID with fallback
    const subTab = useMemo(() => {
        const subTabFromPath = pathParts[subTabSegmentPosition];
        const currentTab = safeList.find(item => item.id === tab);

        if (subTabFromPath && currentTab?.subTabs?.some(item => item.id === subTabFromPath)) {
            return subTabFromPath;
        }
        return currentTab?.subTabs?.[0]?.id || "default";
    }, [pathParts, safeList, tab, subTabSegmentPosition]);

    // Find tab index safely
    const findTabIndex = useCallback(() => {
        const index = safeList.findIndex(item => item?.id === tab);
        return index >= 0 ? index : 0;
    }, [safeList, tab]);

    // Find subtab index safely
    const findSubTabIndex = useCallback((tabId) => {
        const currentTab = safeList.find(item => item?.id === tabId);
        if (!currentTab || !currentTab.subTabs) return []; // No subtabs

        const index = currentTab.subTabs.findIndex(item => item?.id === subTab);
        return index >= 0 ? [index] : [0]; // Return as array for Accordion index prop
    }, [safeList, subTab]);

    const [tabIndex, setTabIndex] = useState([]);
    const [subTabIndices, setSubTabIndices] = useState({});
    const [isNavigating, setIsNavigating] = useState(false);

    // Update tab indices when path changes
    useEffect(() => {
        const index = findTabIndex();
        setTabIndex([index]); // Set as array for Accordion index prop
    }, [findTabIndex]);

    // Update subtab indices when tab or subTab changes
    useEffect(() => {
        if (tab) {
            const currentTab = safeList.find(item => item?.id === tab);
            if (currentTab?.subTabs) {
                const subTabIndex = currentTab.subTabs.findIndex(item => item?.id === subTab);
                if (subTabIndex >= 0) {
                    setSubTabIndices(prev => ({
                        ...prev,
                        [tab]: [subTabIndex]
                    }));
                }
            }
        }
    }, [tab, subTab, safeList]);

    // Fix for IntersectionObserver issues
    useEffect(() => {
        const patchIntersectionObserver = () => {
            try {
                // Only patch if we have the IntersectionObserver
                if (window.IntersectionObserver && !window.IntersectionObserver._patched) {
                    const originalObserve = IntersectionObserver.prototype.observe;

                    IntersectionObserver.prototype.observe = function (target) {
                        if (target && document.body.contains(target)) {
                            return originalObserve.apply(this, arguments);
                        }
                    };

                    window.IntersectionObserver._patched = true;
                }
            } catch (e) {
                console.error("Failed to patch IntersectionObserver", e);
            }
        };

        patchIntersectionObserver();

        return () => {
            // Nothing to clean up
        };
    }, []);

    // In the handleTabChange function of MyAccordionNested.js

    const handleTabChange = useCallback((newTabId, subTabId = null) => {
        if (isNavigating) return; // Prevent multiple rapid clicks

        try {
            setIsNavigating(true);

        // Validate tab exists
        const targetTab = safeList.find(item => item?.id === newTabId);
        if (!targetTab) {
            console.warn(`Tab with id ${newTabId} not found`);
            setIsNavigating(false);
            return;
        }

        // If no subtab is specified, use first available subtab or null
        let effectiveSubTabId = subTabId;
            if (subTabId === null && targetTab?.subTabs && targetTab?.subTabs?.length > 0) {
                effectiveSubTabId = targetTab?.subTabs[0]?.id;
        }

        // Validate subtab exists if specified
        if (effectiveSubTabId !== null && targetTab.subTabs) {
            const targetSubTab = targetTab.subTabs.find(sub => sub?.id === effectiveSubTabId);
            if (!targetSubTab) {
                console.warn(`SubTab with id ${effectiveSubTabId} not found in tab ${newTabId}`);
                // Fall back to first subtab instead of failing
                effectiveSubTabId = targetTab.subTabs[0]?.id;
            }
        }

        // Build navigation path - ensure we don't add parts that are already in the base path
        const basePath = urlBasePath.endsWith('/') ? urlBasePath : `${urlBasePath}/`;

        // Only include these parts if they're not already in the base path
        const aliasPath = includeALias && alias && !basePath.includes(`/${alias}/`) ? `${alias}/` : '';
        const agraphyPath = includeAgraphy && !basePath.includes('/agraphy/') ? 'agraphy/' : '';

        const path = effectiveSubTabId
            ? `${basePath}${aliasPath}${agraphyPath}${newTabId}/${effectiveSubTabId}`
            : `${basePath}${aliasPath}${agraphyPath}${newTabId}`;

        // Clean up the path to avoid double slashes
        const cleanPath = path.replace(/\/+/g, '/');

        // Navigate to new path
        navigate(cleanPath);

            // Reset navigation lock after navigation
            setTimeout(() => setIsNavigating(false), 300);
        } catch (error) {
            console.error('Error navigating to tab:', error);
            setIsNavigating(false);
        }
    }, [navigate, safeList, urlBasePath, includeALias, includeAgraphy, alias, isNavigating]);

    // Handle subtab state changes without navigation
    const handleSubTabIndexChange = useCallback((tabId, expandedIndices) => {
        setSubTabIndices(prev => ({
            ...prev,
            [tabId]: expandedIndices
        }));
    }, []);

    // Custom fallback for Suspense
    const LoadingFallback = () => (
        <Flex justify="center" align="center" p={6} direction="column" gap={3}>
            <Spinner size="lg" color="blue.500" thickness="3px" speed="0.8s" />
            <Text fontSize="md" color="gray.600">Loading content...</Text>
        </Flex>
    );

    // UI Colors based on color mode
    const bgColor = useColorModeValue("white", "gray.800");
    const hoverBgColor = useColorModeValue("gray.50", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const activeBgColor = useColorModeValue("blue.50", "blue.900");
    const activeTextColor = useColorModeValue("blue.600", "blue.200");
    const panelBgColor = useColorModeValue("gray.50", "gray.900");
    const errorColor = useColorModeValue("red.500", "red.300");

    return (
        <Box className="accordion-container" borderRadius="md" overflow="hidden" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
            <Accordion
                allowMultiple={true}
                defaultIndex={tabIndex}
                index={tabIndex}
                onChange={(expandedIndices) => setTabIndex(expandedIndices)}
                className="nested-accordion"
            >
                {safeList.map((item, index) => {
                    // Determine if this tab should be expanded
                    const isExpanded = tabIndex.includes(index);
                    const isActive = item.id === tab;

                    return (
                        <React.Fragment key={`tab-${item?.id || index}`}>
                            <AccordionItem
                                className="nested-accordion-item"
                                border="0"
                                mb="1px"
                            >
                                <AccordionButton
                                        py={4}
                                        px={5}
                                        _hover={{ bg: hoverBgColor }}
                                        bg={isActive ? activeBgColor : bgColor}
                                        color={isActive ? activeTextColor : "inherit"}
                                        fontWeight={isActive ? "600" : "500"}
                                        borderBottom={isExpanded ? `1px solid ${borderColor}` : "none"}
                                        transition="all 0.2s"
                                    onClick={() => {
                                        // Only navigate if not already on this tab
                                        if (item.id !== tab) {
                                            handleTabChange(item.id);
                                        }
                                    }}
                                    isDisabled={isNavigating}
                                >
                                        <Flex flex="1" textAlign="left" align="center">
                                            <Text fontWeight={"bold"} fontSize={'1.3rem'}>{item?.title || "Unnamed Tab"}</Text>
                                            {item?.badge && (
                                                <Badge ml={2} colorScheme={isActive ? "blue" : "gray"} fontSize="xs" borderRadius="full" px={2}>
                                                    {item.badge}
                                                </Badge>
                                            )}
                                        </Flex>
                                        <AccordionIcon transform={isExpanded ? "rotate(180deg)" : "rotate(0deg)"} transition="transform 0.2s" />
                                </AccordionButton>
                                {/* Only render content if expanded to avoid intersection observer issues */}
                                {isExpanded && item?.subTabs && (
                                    <AccordionPanel p={0} bg={panelBgColor}>
                                        <Box p={4}>
                                            {item?.subTabs && Array.isArray(item.subTabs) && item.subTabs.length > 0 ? (
                                            // Render subtabs if they exist
                                                <Accordion
                                                    allowMultiple={true}
                                                    defaultIndex={findSubTabIndex(item.id)}
                                                    index={subTabIndices[item.id] || findSubTabIndex(item.id)}
                                                    onChange={(expandedIndices) => handleSubTabIndexChange(item.id, expandedIndices)}
                                                    className="nested-sub-accordion"
                                                    borderRadius="md"
                                                    overflow="hidden"
                                                    borderWidth="1px"
                                                    borderColor={borderColor}
                                                >
                                                    {item?.subTabs.map((subItem, subIndex) => {
                                                        const isSubTabActive = subItem.id === subTab;
                                                        const isSubTabExpanded = (subTabIndices[item.id] || []).includes(subIndex);

                                                        return (
                                                            <AccordionItem
                                                                key={`subtab-${subItem?.id || subIndex}`}
                                                                className="nested-sub-accordion-item"
                                                                border="0"
                                                                mb="1px"
                                                                _last={{ mb: 0 }}
                                                            >
                                                                <h2>
                                                                    <AccordionButton
                                                                        py={3}
                                                                        px={4}
                                                                        _hover={{ bg: hoverBgColor }}
                                                                        bg={isSubTabActive ? activeBgColor : bgColor}
                                                                        color={isSubTabActive ? activeTextColor : "inherit"}
                                                                        fontWeight={isSubTabActive ? "600" : "normal"}
                                                                        borderBottom={`1px solid ${borderColor}`}
                                                                        borderBottomWidth={isSubTabActive ? "2px" : "1px"}
                                                                        borderBottomColor={isSubTabActive ? "blue.500" : borderColor}
                                                                        transition="all 0.2s"
                                                                        onClick={() => {
                                                                            // Only navigate if not already on this subtab
                                                                            if (!isSubTabActive) {
                                                                                handleTabChange(item.id, subItem.id);
                                                                            }
                                                                        }}
                                                                        isDisabled={isNavigating}
                                                                    >
                                                                        <Flex flex="1" textAlign="left" align="center">
                                                                            <Text fontSize={'1.1rem'}>{subItem?.title || "Unnamed Sub-Tab"}</Text>
                                                                            {subItem?.badge && (
                                                                                <Badge ml={2} colorScheme={isSubTabActive ? "blue" : "gray"} fontSize="xs" borderRadius="full" px={2}>
                                                                                    {subItem.badge}
                                                                                </Badge>
                                                                            )}
                                                                        </Flex>
                                                                        <AccordionIcon
                                                                            transform={isSubTabExpanded ? "rotate(180deg)" : "rotate(0deg)"}
                                                                            transition="transform 0.2s"
                                                                            opacity={isSubTabActive ? 1 : 0.7}
                                                                        />
                                                                    </AccordionButton>
                                                                </h2>
                                                                {isSubTabActive && (
                                                                    <AccordionPanel py={5} px={4} bg={bgColor}>
                                                                        {subItem?.Component ? (
                                                                            <Suspense fallback={<LoadingFallback />}>
                                                                                <subItem.Component
                                                                                    currentTabId={tab}
                                                                                    currentSubTabId={subTab}
                                                                                    alias={alias}
                                                                                    urlBasePath={urlBasePath}
                                                                                />
                                                                            </Suspense>
                                                                        ) : (
                                                                            <Flex
                                                                                color={errorColor}
                                                                                p={4}
                                                                                borderRadius="md"
                                                                                bg="red.50"
                                                                                borderWidth="1px"
                                                                                borderColor="red.200"
                                                                                align="center"
                                                                                justify="center"
                                                                            >
                                                                                <Text fontWeight="medium">Component not found</Text>
                                                                            </Flex>
                                                                        )}
                                                                    </AccordionPanel>
                                                                )}
                                                            </AccordionItem>
                                                        );
                                                    })}
                                                </Accordion>
                                            ) : item?.Component ? (
                                                // Render main component if no subtabs
                                                <Suspense fallback={<LoadingFallback />}>
                                                    <Box p={2}>
                                                        <item.Component
                                                            currentTabId={tab}
                                                            alias={alias}
                                                            urlBasePath={urlBasePath}
                                                        />
                                                    </Box>
                                                </Suspense>
                                            ) : (
                                                <Flex
                                                    color={errorColor}
                                                    p={4}
                                                    borderRadius="md"
                                                    bg="red.50"
                                                    borderWidth="1px"
                                                    borderColor="red.200"
                                                    align="center"
                                                    justify="center"
                                                >
                                                    <Text fontWeight="medium">Component not found</Text>
                                                </Flex>
                                            )}
                                        </Box>
                                    </AccordionPanel>
                                )}
                            </AccordionItem>
                            {safeList.length - 1 !== index && <Divider orientation='horizontal' />}
                        </React.Fragment>
                    );
                })}
            </Accordion>
        </Box>
    );
};

export default MyAccordionNested;